/** @format */

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  exportReturningApplicantsExcel,
  fetchReturningApplicants,
} from "../../adminApi";
import { ReturningApplicantRowDto } from "../../models";
import type { AxiosResponse } from "axios";

function getResponseContentType(response: AxiosResponse): string {
  const h = response.headers;
  if (!h) return "";
  const anyH = h as {
    get?: (name: string) => string | undefined;
    "content-type"?: string;
    "Content-Type"?: string;
  };
  if (typeof anyH.get === "function") {
    const v =
      anyH.get("content-type") ??
      anyH.get("Content-Type") ??
      (h as { getContentType?: () => string }).getContentType?.();
    if (v) return v;
  }
  return anyH["content-type"] ?? anyH["Content-Type"] ?? "";
}

async function messageFromErrorBlob(blob: Blob): Promise<string | null> {
  try {
    const text = await blob.text();
    if (!text.trim()) return null;
    if (!text.trim().startsWith("{")) {
      return text.length > 400 ? `${text.slice(0, 400)}…` : text;
    }
    const body = JSON.parse(text) as Record<string, unknown>;
    const validation = (body.validation ?? body.Validation) as
      | { messages?: unknown; Messages?: unknown }
      | undefined;
    const messages = validation?.messages ?? validation?.Messages;
    if (Array.isArray(messages)) return messages.map(String).join(" ");
    const title = body.title ?? body.Title;
    if (typeof title === "string") return title;
    return null;
  } catch {
    return null;
  }
}

/** .xlsx is ZIP; detect so we don't treat JSON error blobs as spreadsheets. */
async function blobLooksLikeZip(blob: Blob): Promise<boolean> {
  if (blob.size < 2) return false;
  const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
  return head[0] === 0x50 && head[1] === 0x4b;
}

function coerceValidationCode(raw: unknown): number | undefined {
  if (raw === undefined || raw === null) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function formatStage(raw: string) {
  return raw.replace(/([A-Z])/g, " $1").trim();
}

/** Accept camelCase or PascalCase API envelopes; tolerate string status codes. */
function parseReturningApplicantsResponse(payload: unknown): {
  rows: ReturningApplicantRowDto[] | null;
  code: number | undefined;
  message: string | undefined;
} {
  if (!payload || typeof payload !== "object") {
    return { rows: null, code: undefined, message: undefined };
  }
  const o = payload as Record<string, unknown>;
  const rawList = o.data ?? o.Data;
  const validation = (o.validation ?? o.Validation) as
    | Record<string, unknown>
    | undefined;

  const code = coerceValidationCode(validation?.code ?? validation?.Code);
  const messages = validation?.messages ?? validation?.Messages;
  const message = Array.isArray(messages)
    ? messages.map(String).join(" ")
    : messages != null
      ? String(messages)
      : undefined;

  if (!Array.isArray(rawList)) {
    return { rows: null, code, message };
  }

  return {
    rows: rawList as ReturningApplicantRowDto[],
    code,
    message,
  };
}

type Props = {
  open: boolean;
  onClose: () => void;
  intakeYear: number;
  cohortLabel: string;
};

export default function ReturningApplicantsModal({
  open,
  onClose,
  intakeYear,
  cohortLabel,
}: Props) {
  const [rows, setRows] = useState<ReturningApplicantRowDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchReturningApplicants(intakeYear);
      const { rows: dataArr, code, message } =
        parseReturningApplicantsResponse(res);

      if (!Array.isArray(dataArr)) {
        setError(
          message ||
            (typeof res === "object" &&
            res !== null &&
            "title" in res &&
            typeof (res as { title?: string }).title === "string"
              ? (res as { title: string }).title
              : null) ||
            "Unable to load returning applicants."
        );
        setRows([]);
        return;
      }

      const ok =
        code === undefined ||
        code === 200 ||
        code === 201 ||
        (code !== undefined && code >= 200 && code < 300);

      if (!ok) {
        setError(message || "Unable to load returning applicants.");
        setRows([]);
        return;
      }

      setRows(dataArr);
      setPage(0);
    } catch (e: unknown) {
      console.error(e);
      const ax = e as {
        response?: { data?: unknown; status?: number };
        message?: string;
      };
      let detail = "Unable to load returning applicants.";
      const d = ax.response?.data;
      if (typeof d === "string" && d.trim()) {
        detail = d.length > 200 ? `${d.slice(0, 200)}…` : d;
      } else if (d && typeof d === "object") {
        const v = (d as Record<string, unknown>).validation as
          | { messages?: unknown }
          | undefined;
        if (v?.messages && Array.isArray(v.messages)) {
          detail = v.messages.map(String).join(" ");
        }
      } else if (ax.message) {
        detail = ax.message;
      }
      setError(detail);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [intakeYear]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  const handleExportExcel = async () => {
    setExporting(true);
    setError(null);
    try {
      const response = await exportReturningApplicantsExcel(intakeYear);
      const blob = response.data as Blob;
      const contentType = getResponseContentType(response).toLowerCase();
      const declaredJson =
        contentType.includes("application/json") ||
        contentType.includes("problem+json");

      if (response.status >= 400 || declaredJson) {
        setError(
          (await messageFromErrorBlob(blob)) ??
            `Export failed (${response.status}).`
        );
        return;
      }

      if (blob.size === 0) {
        setError("The server returned an empty file. Please try again.");
        return;
      }

      if (!(await blobLooksLikeZip(blob))) {
        const asMsg = await messageFromErrorBlob(blob);
        if (asMsg) {
          setError(asMsg);
          return;
        }
        setError("Export did not return a valid Excel file. Please try again.");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().split("T")[0];
      link.href = url;
      link.setAttribute(
        "download",
        `Returning-students-${intakeYear}-${timestamp}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const pagedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <ReplayOutlinedIcon color="info" />
        <Box>
          <Typography component="span" variant="h6" fontWeight={700}>
            Returning students ({cohortLabel})
          </Typography>
          <Typography variant="body2" color="text.secondary" display="block">
            Intake year {intakeYear} •{" "}
            {loading
              ? "Loading…"
              : `${rows.length.toLocaleString()} applicants`}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
        {loading && (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && rows.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No returning applicants for this year.
          </Typography>
        )}
        {!loading && rows.length > 0 && (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Application ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Surname</TableCell>
                    <TableCell>ID number</TableCell>
                    <TableCell>Gender</TableCell>
                    <TableCell>Race</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell align="center">Completed</TableCell>
                    <TableCell align="center">Assessed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((r) => (
                    <TableRow key={r.applicationId}>
                      <TableCell>{r.applicationId}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.surname}</TableCell>
                      <TableCell>{r.identityNumber}</TableCell>
                      <TableCell>{r.gender}</TableCell>
                      <TableCell>{r.race}</TableCell>
                      <TableCell>{formatStage(r.applicationStage)}</TableCell>
                      <TableCell align="center">
                        {r.dateCompleted
                          ? new Date(r.dateCompleted).toLocaleDateString(
                              "en-ZA"
                            )
                          : "—"}
                      </TableCell>
                      <TableCell align="center">
                        {r.isAssessed ? "Yes" : "No"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              labelRowsPerPage="Rows:"
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: "wrap" }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ flex: 1, flexWrap: "wrap", mr: "auto" }}
        >
          <Button
            variant="contained"
            startIcon={
              exporting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
            onClick={() => void handleExportExcel()}
            disabled={loading || exporting}
          >
            {exporting ? "Exporting…" : "Export to Excel"}
          </Button>
          <Button variant="outlined" onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        </Stack>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
